import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import * as S from './Common.styled';

/**
 * @author Burak
 */

const ForgotPassword = () => {
	const schema = useMemo(
		() =>
			yup.object().shape({
				email: yup.string().email().nullable().required()
			}),
		[]
	);

	const { LangCountryCode } = useLocale();

	const { showError, showSuccess } = useAlert();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const {
		state: { from }
	} = useLocation();

	useEffect(() => {
		if (from !== 'login') navigate({ pathname: `/login/${LangCountryCode}`, search: searchParams.toString() });
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});

	const {
		handleSubmit,
		watch,
		setFocus,
		formState: { isDirty, isSubmitting }
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			await openAxios.post(OPEN.PASSWORD_RESET, data);
			showSuccess();
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const handleKeyPress = ({ charCode }) => {
		// Enter key
		if (charCode === 13) {
			if (!watch('email')) {
				setFocus('email');
			} else {
				handleSubmit(onSubmit, (errors) => {
					AppLogger.log(errors);
				})();
			}
		}
	};

	return (
		<>
			<Form
				formMethods={formMethods}
				borderless
				style={{
					display: 'flex',
					justifyContent: 'center'
				}}>
				<S.ForgotPasswordFormContainer>
					<S.Container>
						<S.Content>
							<FormItem>
								<Typography variant="h3" fontWeight="500" gutterBottom noWrap>
									forgotPassword
								</Typography>
							</FormItem>
							<FormItem label="email">
								<StringInput name="email" required onKeyPress={handleKeyPress} />
							</FormItem>

							<FormItem style={{ paddingTop: '2rem' }}>
								<Button
									size="large"
									loading={isSubmitting}
									disabled={!isDirty}
									fullWidth
									onClick={handleSubmit(onSubmit, (errors) => {
										AppLogger.log(errors);
									})}>
									<Typography className="button-text" fontWeight="500">
										sendEmail
									</Typography>
								</Button>
							</FormItem>
						</S.Content>
					</S.Container>
					<FormItem>
						<S.ButtonContainer>
							<Typography color="var(--black)">alreadyRememberPassword</Typography>
							<S.ButtonLink
								variant="transparent"
								label="signIn"
								disabled={isSubmitting}
								onClick={() => {
									navigate({
										pathname: `/login/${LangCountryCode}`,
										search: searchParams.toString()
									});
								}}
							/>
						</S.ButtonContainer>
					</FormItem>
				</S.ForgotPasswordFormContainer>
			</Form>
		</>
	);
};

export default ForgotPassword;
